import useGetEstimatePreview from '@/api/queries/estimates/useGetEstimatePreview';
import useGetMyRegionPreferences from '@/api/queries/me/useGetMyRegionPreferences';
import { Badge, EmptyState, UntitledLoading } from '@payaca/ambrosia';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';
import { FC } from 'react';
import useLivePreview from '../useLivePreview';

type EstimatePreviewPaneProps = {
  estimateId: string;
};

const EstimatePreviewPane: FC<EstimatePreviewPaneProps> = (props) => {
  const { estimateId } = props;
  const { preview, isLoading, isError, isOutOfSync } =
    useGetEstimatePreview(estimateId);
  const { data: regionPreferences } = useGetMyRegionPreferences();
  const hostRef = useLivePreview({
    template: preview?.template ?? '',
    context: preview?.context,
    locale: regionPreferences?.locale,
  });

  if (isLoading) {
    return (
      <div className="relative min-h-[400px] rounded-md bg-white shadow-md">
        <LoaderOverlay />
      </div>
    );
  }

  if (isError || !preview) {
    return (
      <div className="flex min-h-[400px] items-center justify-center rounded-md bg-white shadow-md">
        <EmptyState text="Preview unavailable" />
      </div>
    );
  }

  return (
    <div className="relative">
      <div
        ref={hostRef}
        data-testid="estimate-document-preview"
        className="pointer-events-none overflow-hidden rounded-lg bg-white shadow-md border"
      />
      {isOutOfSync && (
        <div className="pointer-events-none absolute left-3 top-3">
          <Badge
            variant="white"
            size="sm"
            className="flex items-center gap-1.5"
          >
            <UntitledLoading className="size-3" />
            Syncing…
          </Badge>
        </div>
      )}
    </div>
  );
};

export default EstimatePreviewPane;
